import React, { useState, useEffect } from "react";

const inputCls =
  "w-full rounded-xl bg-[#080B14] border border-white/10 px-4 py-3 text-sm text-white " +
  "placeholder-white/25 focus:outline-none focus:border-[#D4AF37]/50 transition-colors";

export default function InquiryModal({
  isOpen,
  onClose,
  title,
  subtitle,
  recipient,
  fields,
  defaultValues,
  submitLabel = "Submit",
  onSubmit,       // optional — async (formData) => void; if omitted, falls back to no-op
}) {
  const blank = () =>
    Object.fromEntries(
      fields.map((f) => [f.name, (defaultValues && defaultValues[f.name]) || ""])
    );
  const [form, setForm] = useState(blank);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  useEffect(() => {
    if (isOpen) { setForm(blank()); setStatus("idle"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      if (onSubmit) await onSubmit({ ...form });
      setStatus("success");
      } catch (err) {
        console.error("InquiryModal submit error:", err);
        alert(err?.message || "Submission failed");
        setStatus("error");
      }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0C111D] border border-white/10 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="px-8 pt-8 flex items-start justify-between gap-4">
          <div>
            <div className="font-display text-2xl text-white">{title}</div>
            {subtitle && (
              <p className="text-white/50 text-sm mt-1.5 leading-relaxed">{subtitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="mt-1 flex-shrink-0 text-white/30 hover:text-white/70 transition-colors"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Success */}
        {status === "success" ? (
          <div className="px-8 py-12 text-center">
            <div className="text-[#4AA36A] font-display text-5xl mb-4">✓</div>
            <div className="font-display text-xl text-white mb-2">Sent.</div>
            <p className="text-white/50 text-sm">We'll be in touch soon.</p>
            <button
              onClick={onClose}
              className="mt-8 rounded-full border border-white/15 text-white/60 px-6 py-2.5 text-sm hover:border-white/30 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-8 py-8 space-y-4">

            {/* To: display row */}
            {recipient && (
              <div>
                <div className="text-xs tracking-[0.16em] uppercase text-white/50 mb-2">To</div>
                <div className="rounded-xl bg-[#080B14] border border-white/8 px-4 py-3 text-sm text-[#D4AF37] font-display">
                  {recipient}
                </div>
              </div>
            )}

            {fields.map((f) => (
              <div key={f.name}>
                <label
                  htmlFor={`modal-${f.name}`}
                  className="block text-xs tracking-[0.16em] uppercase text-white/50 mb-2"
                >
                  {f.label}
                  {f.required && <span className="text-[#D4AF37] ml-0.5">*</span>}
                </label>

                {f.type === "textarea" ? (
                  <textarea
                    id={`modal-${f.name}`}
                    name={f.name}
                    placeholder={f.placeholder}
                    value={form[f.name] || ""}
                    onChange={handleChange}
                    required={f.required}
                    rows={f.rows || 4}
                    className={inputCls + " resize-none"}
                  />
                ) : f.type === "select" ? (
                  <select
                    id={`modal-${f.name}`}
                    name={f.name}
                    value={form[f.name] || ""}
                    onChange={handleChange}
                    required={f.required}
                    className={inputCls + " appearance-none"}
                  >
                    <option value="" disabled>Select one…</option>
                    {f.options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`modal-${f.name}`}
                    name={f.name}
                    type={f.type || "text"}
                    placeholder={f.placeholder}
                    value={form[f.name] || ""}
                    onChange={handleChange}
                    required={f.required}
                    className={inputCls}
                  />
                )}
              </div>
            ))}

            {status === "error" && (
              <p className="text-red-400 text-xs pt-1">
                Something went wrong — please try again.
              </p>
            )}

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-white/35 hover:text-white/60 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#080B14] font-semibold px-6 py-3 text-sm hover:scale-[1.02] transition-transform disabled:opacity-60 disabled:scale-100"
              >
                {status === "loading" ? (
                  <>
                    <svg
                      className="h-4 w-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    Sending…
                  </>
                ) : (
                  submitLabel
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
